/**
 * Reporte de Pagos: tarjetas resumen, filtros, lista de contratos ordenada
 * por fecha de evento más próxima, con historial de abonos (registrar,
 * editar, eliminar con confirmación) y recibo PDF por cada pago.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  contractAbonos, totalAbonado, totalContratado, saldoPendiente, balanceAfterAbono,
  paymentStatus, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS,
  filterByPaymentFilter, sortByUpcomingEventDate, FORMA_PAGO_LABELS,
} from "./utils";
import { formatEventDateLong } from "../../lib/dateUtils";

const money = (n) => `$${(n || 0).toLocaleString()}`;
const todayLocalStr = () => {
  const d = new Date();
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const emptyAbonoForm = () => ({ monto: "", fecha: todayLocalStr(), forma_pago: "efectivo", nota: "" });

const FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "pendiente", label: "Con saldo pendiente" },
  { value: "pagado", label: "Pagados" },
  { value: "este_mes", label: "Eventos de este mes" },
];

export function ReportePagos({ contracts, generateRecibo, addAbono, editAbono, removeAbono, toggleContractCancelled, generateAbonoRecibo, DatePicker }) {
  const [filter, setFilter] = useState("todos");
  const [payDialog, setPayDialog] = useState(null); // { contract, abono: null|existingAbono }
  const [form, setForm] = useState(emptyAbonoForm());

  const activeContracts = contracts.filter((c) => c.status !== "cancelled");
  const totalContratadoSum = activeContracts.reduce((s, c) => s + totalContratado(c), 0);
  const totalCobradoSum = activeContracts.reduce((s, c) => s + totalAbonado(c), 0);
  const saldoPorCobrarSum = activeContracts.reduce((s, c) => s + saldoPendiente(c), 0);

  const visibleContracts = filterByPaymentFilter(contracts, filter).sort(sortByUpcomingEventDate);

  const openNewPayment = (contract) => { setForm(emptyAbonoForm()); setPayDialog({ contract, abono: null, liquidar: false }); };
  const openLiquidar = (contract) => {
    setForm({ ...emptyAbonoForm(), monto: String(Math.round(saldoPendiente(contract) * 100) / 100) });
    setPayDialog({ contract, abono: null, liquidar: true });
  };
  const openEditPayment = (contract, abono) => {
    setForm({ monto: abono.monto, fecha: abono.fecha, forma_pago: abono.forma_pago || "efectivo", nota: abono.nota || "" });
    setPayDialog({ contract, abono, liquidar: false });
  };
  const closeDialog = () => setPayDialog(null);

  const savePayment = async () => {
    if (!payDialog) return;
    const monto = parseFloat(form.monto);
    if (!monto || monto <= 0) { toast.error("El monto debe ser mayor a 0"); return; }
    if (!form.fecha) { toast.error("Elige la fecha de pago"); return; }
    const payload = { monto, fecha: form.fecha, forma_pago: form.forma_pago, nota: form.nota || null };
    const ok = payDialog.abono
      ? await editAbono(payDialog.contract.id, payDialog.abono.id, payload)
      : await addAbono(payDialog.contract.id, payload);
    if (ok) closeDialog();
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="reporte-pagos-summary">
        <Card className="card-premium border-gold/30">
          <CardContent className="p-4">
            <p className="text-pearl-muted text-xs uppercase tracking-wide">Total contratado</p>
            <p className="text-pearl text-2xl font-bold" data-testid="summary-total-contratado">{money(totalContratadoSum)}</p>
          </CardContent>
        </Card>
        <Card className="card-premium border-gold/30">
          <CardContent className="p-4">
            <p className="text-pearl-muted text-xs uppercase tracking-wide">Total cobrado</p>
            <p className="text-green-400 text-2xl font-bold" data-testid="summary-total-cobrado">{money(totalCobradoSum)}</p>
          </CardContent>
        </Card>
        <Card className="card-premium border-gold/30">
          <CardContent className="p-4">
            <p className="text-pearl-muted text-xs uppercase tracking-wide">Saldo por cobrar</p>
            <p className="text-red-400 text-2xl font-bold" data-testid="summary-saldo-por-cobrar">{money(saldoPorCobrarSum)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="reporte-pagos-filters">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={filter === f.value ? "default" : "outline"}
            className={filter === f.value ? "btn-gold" : "btn-gold-outline"}
            onClick={() => setFilter(f.value)}
            data-testid={`pagos-filter-${f.value}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        {visibleContracts.length === 0 && (
          <Card className="card-premium border-dashed">
            <CardContent className="p-8 text-center">
              <p className="text-pearl-muted" data-testid="pagos-no-results">No hay contratos para este filtro</p>
            </CardContent>
          </Card>
        )}
        {visibleContracts.map((contract) => {
          const status = paymentStatus(contract);
          const abonos = contractAbonos(contract);
          const saldo = saldoPendiente(contract);
          const cancelado = contract.status === "cancelled";
          return (
            <Card key={contract.id} className="card-premium" data-testid={`pago-card-${contract.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-start flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-gold font-mono font-bold text-sm bg-gold/10 border border-gold/30 px-2 py-0.5 rounded">{contract.folio || "Sin folio"}</span>
                      <Badge className={PAYMENT_STATUS_COLORS[status]} data-testid={`pago-status-${contract.id}`}>{PAYMENT_STATUS_LABELS[status]}</Badge>
                    </div>
                    <h3 className="text-pearl font-bold">{contract.client_name}</h3>
                    <p className="text-pearl-muted text-sm">{contract.client_phone} · Evento: {contract.event_date ? formatEventDateLong(contract.event_date) : "sin fecha"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-pearl-muted text-xs">Contratado: <span className="text-pearl font-semibold">{money(totalContratado(contract))}</span></p>
                    <p className="text-pearl-muted text-xs">Cobrado: <span className="text-green-400 font-semibold">{money(totalAbonado(contract))}</span></p>
                    <p className="text-pearl-muted text-xs">Saldo: <span className={`font-bold ${saldo > 0 ? "text-red-400" : "text-green-400"}`}>{money(saldo)}</span></p>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" className="btn-gold-outline" onClick={() => openNewPayment(contract)} data-testid={`registrar-pago-btn-${contract.id}`}>
                    + Registrar pago
                  </Button>
                  {saldo > 0 && !cancelado && (
                    <Button size="sm" className="bg-green-600/90 hover:bg-green-600 text-white" onClick={() => openLiquidar(contract)} data-testid={`liquidar-btn-${contract.id}`}>
                      Liquidar ({money(saldo)})
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className={cancelado ? "border-green-500/50 text-green-400" : "border-gray-500/50 text-gray-300"}
                    onClick={() => toggleContractCancelled(contract)}
                    data-testid={`toggle-cancelado-btn-${contract.id}`}
                  >
                    {cancelado ? "Reactivar" : "Marcar cancelado"}
                  </Button>
                  {status === "pagado" && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => generateRecibo(contract)} data-testid={`recibo-final-btn-${contract.id}`}>
                      Recibo final
                    </Button>
                  )}
                </div>

                {abonos.length > 0 && (
                  <div className="border-t border-pearl-muted/20 pt-2 space-y-1">
                    {abonos.map((a) => (
                      <div key={a.id} className="flex items-center justify-between gap-2 text-sm flex-wrap" data-testid={`abono-row-${a.id}`}>
                        <span className="text-pearl-muted">{a.fecha} · {FORMA_PAGO_LABELS[a.forma_pago] || a.forma_pago}{a.nota ? ` · ${a.nota}` : ""}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-green-400 font-semibold">{money(a.monto)}</span>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-pearl-muted" onClick={() => openEditPayment(contract, a)} data-testid={`abono-edit-btn-${a.id}`}>Editar</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400" onClick={() => removeAbono(contract.id, a.id)} data-testid={`abono-delete-btn-${a.id}`}>Eliminar</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-gold" onClick={() => generateAbonoRecibo(contract, a, balanceAfterAbono(contract, a.id))} data-testid={`abono-recibo-btn-${a.id}`}>Recibo</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {payDialog && (
        <Dialog open={!!payDialog} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent className="bg-night border-gold/30 text-pearl max-w-md" data-testid="abono-dialog">
            <DialogHeader>
              <DialogTitle className="text-gold">{payDialog.abono ? "Editar pago" : payDialog.liquidar ? "Liquidar" : "Registrar pago"} - {payDialog.contract.folio}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-pearl">Monto</Label>
                <Input type="number" value={form.monto} className="input-premium" data-testid="abono-monto-input"
                  onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))} />
              </div>
              <div>
                <Label className="text-pearl">Fecha de pago</Label>
                {DatePicker ? (
                  <DatePicker value={form.fecha} onChange={(d) => setForm((f) => ({ ...f, fecha: d }))} placeholder="Fecha de pago" />
                ) : (
                  <Input type="date" value={form.fecha} className="input-premium" onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} />
                )}
              </div>
              <div>
                <Label className="text-pearl">Método de pago</Label>
                <Select value={form.forma_pago} onValueChange={(v) => setForm((f) => ({ ...f, forma_pago: v }))}>
                  <SelectTrigger className="input-premium" data-testid="abono-forma-pago-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="deposito">Depósito</SelectItem>
                    <SelectItem value="no_especificado">No especificado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-pearl">Nota (opcional)</Label>
                <Input value={form.nota} className="input-premium" data-testid="abono-nota-input"
                  onChange={(e) => setForm((f) => ({ ...f, nota: e.target.value }))} />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" className="text-pearl-muted" onClick={closeDialog}>Cancelar</Button>
                <Button className="btn-gold" onClick={savePayment} data-testid="abono-save-btn">Guardar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
